import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Building2, Check, CheckCircle2, ClipboardCopy, LibraryBig,
  Clock3, KeyRound, Plus, RefreshCw, ShieldCheck, UserMinus, UsersRound, X,
} from "lucide-react";
import { auth } from "./firebaseConfig";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import {
  approveOrganisationRequest, createOrganisation, declineOrganisationRequest,
  leaveOrganisation, listMyOrganisations, listOrganisationMembers,
  listPendingOrganisationRequests, removeOrganisationMember,
  requestOrganisationJoin, rotateOrganisationCode,
} from "./utils/organisationApi";
import "./Organisations.css";

const isManager = (role) => role === "owner" || role === "admin";
const roleLabel = (role) => ({ owner: "Owner", admin: "Admin", member: "Member" }[role] || "Member");
const personName = (person) => typeof person?.displayName === "string" && person.displayName.trim()
  ? person.displayName.trim() : "Teacher name unavailable";

const joinErrorMessage = (error) => {
  if (error?.code === "already-member") return "You are already a member of this organisation.";
  if (error?.code === "code-not-found" || error?.code === "invalid-code") {
    return "This code is invalid or no longer active. Ask an organisation manager for the current code.";
  }
  if (error?.code === "rate-limited") return "Too many join attempts. Please wait about 10 minutes before trying again.";
  return error?.message || "We could not submit your request. Please try again.";
};

const feedbackText = (error) => error?.message || "Something went wrong. Please try again.";

function ConfirmationDialog({ action, busy, onCancel, onConfirm }) {
  if (!action) return null;
  return (
    <div className="org-dialog-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onCancel();
    }}>
      <div className="org-dialog" role="alertdialog" aria-modal="true" aria-labelledby="org-dialog-title"
        aria-describedby="org-dialog-description">
        <span className={`org-dialog-icon${action.danger ? " is-danger" : ""}`}>
          {action.danger ? <UserMinus size={24} /> : <RefreshCw size={24} />}
        </span>
        <h2 id="org-dialog-title">{action.title}</h2>
        <p id="org-dialog-description">{action.description}</p>
        <div className="org-dialog-actions">
          <button type="button" className="org-button org-button-quiet" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className={`org-button ${action.danger ? "org-button-danger" : "org-button-primary"}`}
            onClick={onConfirm} disabled={busy} autoFocus>
            {busy ? "Working…" : action.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Organisations({ user }) {
  const navigate = useNavigate();
  const { organisationId } = useParams();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [search, setSearch] = useState("");
  const [organisations, setOrganisations] = useState([]);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState("");
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [formBusy, setFormBusy] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [codeOverride, setCodeOverride] = useState("");
  const formLock = useRef(false);
  const actionLock = useRef(false);
  const loadRun = useRef(0);

  const current = organisations.find((item) => item.organisationId === organisationId);

  useEffect(() => {
    const syncTheme = () => {
      const next = localStorage.getItem("darkMode") === "true";
      setDarkMode(next);
      document.body.classList.toggle("dark-mode", next);
      document.documentElement.setAttribute("data-dark-mode", next ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    localStorage.setItem("darkMode", String(next));
    setDarkMode(next);
    document.body.classList.toggle("dark-mode", next);
    document.documentElement.setAttribute("data-dark-mode", next ? "true" : "false");
  };

  const load = useCallback(async (showSpinner = true) => {
    const run = ++loadRun.current;
    if (showSpinner) {
      setLoading(true);
      setOrganisations([]);
    }
    setMembers([]);
    setRequests([]);
    setError("");
    setLoadFailed(false);
    try {
      const mine = await listMyOrganisations();
      if (run !== loadRun.current) return;
      const items = Array.isArray(mine.organisations) ? mine.organisations : [];
      setOrganisations(items);
      setCodeOverride("");
      const selected = items.find((item) => item.organisationId === organisationId);
      if (selected && isManager(selected.role)) {
        setDetailLoading(true);
        const [memberData, requestData] = await Promise.all([
          listOrganisationMembers(organisationId),
          listPendingOrganisationRequests(organisationId),
        ]);
        if (run !== loadRun.current) return;
        setMembers(Array.isArray(memberData.members) ? memberData.members : []);
        setRequests(Array.isArray(requestData.requests) ? requestData.requests : []);
      } else {
        setMembers([]);
        setRequests([]);
      }
    } catch (loadError) {
      if (run === loadRun.current) {
        setError(feedbackText(loadError));
        setLoadFailed(true);
      }
    } finally {
      if (run === loadRun.current) {
        setLoading(false);
        setDetailLoading(false);
      }
    }
  }, [organisationId]);

  useEffect(() => {
    load();
    return () => { loadRun.current += 1; };
  }, [load]);

  useEffect(() => { setCodeOverride(""); setConfirmation(null); setSearch(""); }, [organisationId]);

  const submitCreate = async (event) => {
    event.preventDefault();
    if (formLock.current) return;
    setLoadFailed(false);
    const normalized = name.normalize("NFC").trim().replace(/\s+/gu, " ");
    const unsafeCharacter = [...normalized].some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 || character === "<" || character === ">";
    });
    if (normalized.length < 2 || normalized.length > 120 || unsafeCharacter) {
      setError("Use a name of 2–120 characters without angle brackets or control characters.");
      return;
    }
    formLock.current = true;
    setFormBusy("create");
    setError("");
    setNotice("");
    try {
      const created = await createOrganisation(normalized);
      setName("");
      await load(false);
      navigate(`/organisations/${encodeURIComponent(created.organisationId)}`);
      setNotice("Organisation created. You are its owner.");
    } catch (createError) {
      setError(feedbackText(createError));
    } finally {
      formLock.current = false;
      setFormBusy("");
    }
  };

  const submitJoin = async (event) => {
    event.preventDefault();
    if (formLock.current) return;
    setLoadFailed(false);
    if (!joinCode.trim()) {
      setError("Enter the join code shared by an organisation manager.");
      return;
    }
    formLock.current = true;
    setFormBusy("join");
    setError("");
    setNotice("");
    try {
      const result = await requestOrganisationJoin(joinCode.trim());
      setJoinCode("");
      setNotice(result.alreadyPending
        ? "Your request is already pending. An owner or admin must approve it before you become a member."
        : "Request submitted. An owner or admin must approve it before you become a member.");
      await load(false);
    } catch (joinError) {
      setError(joinErrorMessage(joinError));
    } finally {
      formLock.current = false;
      setFormBusy("");
    }
  };

  const runAction = async (action) => {
    if (actionLock.current || !current) return;
    actionLock.current = true;
    setActionBusy(true);
    setError("");
    setLoadFailed(false);
    setNotice("");
    try {
      let rotatedCode = "";
      if (action.type === "approve") await approveOrganisationRequest(current.organisationId, action.requestId);
      if (action.type === "decline") await declineOrganisationRequest(current.organisationId, action.requestId);
      if (action.type === "remove") await removeOrganisationMember(current.organisationId, action.userUid);
      if (action.type === "leave") await leaveOrganisation(current.organisationId);
      if (action.type === "rotate") {
        const result = await rotateOrganisationCode(current.organisationId);
        rotatedCode = result.joinCode;
      }
      setConfirmation(null);
      if (action.type === "leave") navigate("/organisations");
      else await load(false);
      if (rotatedCode) setCodeOverride(rotatedCode);
      setNotice({
        approve: "Join request approved.", decline: "Join request declined.",
        remove: "Member removed.", leave: "You have left the organisation.",
        rotate: "New code ready. The previous join code is no longer valid.",
      }[action.type]);
    } catch (actionError) {
      setConfirmation(null);
      await load(false);
      setError(feedbackText(actionError));
      setLoadFailed(false);
    } finally {
      actionLock.current = false;
      setActionBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(codeOverride || current.joinCode);
      setNotice("Join code copied. Share it with teachers you want to invite.");
    } catch (_error) {
      setLoadFailed(false);
      setError("Could not copy the code. Select and copy it manually.");
    }
  };

  const openAction = (type, payload = {}) => {
    const account = personName(payload);
    const options = {
      decline: { title: "Decline this request?", description: `The request from ${account} will be declined.`, confirmLabel: "Decline request", danger: true },
      remove: { title: "Remove this member?", description: `${account} will lose access to this organisation.`, confirmLabel: "Remove member", danger: true },
      leave: { title: "Leave this organisation?", description: `You will leave ${current.name}. A manager must approve a new request if you want to rejoin.`, confirmLabel: "Leave organisation", danger: true },
      rotate: { title: "Rotate the join code?", description: "The current code will stop working immediately. Share the new code with colleagues who still need to join.", confirmLabel: "Rotate code", danger: false },
    };
    setConfirmation({ type, ...payload, ...options[type] });
  };

  const username = accountDisplayName(user);
  const filtered = organisations.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  const filteredMembers = members.filter((item) => `${personName(item)} ${item.role}`.toLowerCase().includes(search.toLowerCase()));
  const filteredRequests = requests.filter((item) => personName(item).toLowerCase().includes(search.toLowerCase()));
  const showDetail = Boolean(organisationId);

  return (
    <div className={`sd-dashboard sd-brand-theme sd-role-teacher org-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role="teacher" username={username} onLogout={async () => {
        try { await auth.signOut(); navigate("/"); } catch (_error) { setLoadFailed(false); setError("Could not sign out. Please try again."); }
      }} officialDashboardBrand creditsRemaining={user?.creditsRemaining} teacherCode={user?.teacherCode}
        copied={copiedTeacherCode} onCopyCode={async () => {
          if (!user?.teacherCode) return;
          try {
            await navigator.clipboard.writeText(user.teacherCode);
            setCopiedTeacherCode(true);
            window.setTimeout(() => setCopiedTeacherCode(false), 1800);
          } catch (_error) { setLoadFailed(false); setError("Could not copy your teacher code."); }
        }} open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePath="/organisations" />
      <div className="sd-workspace org-workspace">
        <DashboardHeader username={username} role="teacher" darkMode={darkMode} onToggleTheme={toggleTheme}
          onOpenMenu={() => setSidebarOpen(true)} search={search} onSearch={setSearch}
          searchPlaceholder={showDetail ? "Search members and requests..." : "Search organisations..."} />
        <main className="sd-main org-main">
          <section className="org-hero">
            <div className="org-hero-copy">
              <p className="org-eyebrow">Teacher workspace</p>
              <h1>{showDetail && current ? current.name : "Organisations"}</h1>
              <p>{showDetail ? "Your organisation, its people and invitations in one place." : "Bring teachers together in a shared organisation."}</p>
            </div>
            <div className="org-hero-art" aria-hidden="true"><Building2 size={52} /><span><UsersRound size={22} /></span></div>
          </section>

          {error && <div className="org-message org-message-error" role="alert"><X size={18} /><span>{error}</span>{loadFailed && <button type="button" onClick={() => load()} disabled={loading}>Retry</button>}</div>}
          {notice && <div className="org-message org-message-success" role="status"><CheckCircle2 size={18} /><span>{notice}</span><button type="button" aria-label="Dismiss message" onClick={() => setNotice("")}><X size={16} /></button></div>}

          {showDetail ? (
            <>
              <button type="button" className="org-back" onClick={() => navigate("/organisations")}><ArrowLeft size={17} /> All organisations</button>
              {loading ? <div className="org-panel org-state">Loading organisation…</div> : !current ? (
                <div className="org-panel org-state"><Building2 size={28} /><h2>Organisation unavailable</h2><p>It is not in your current memberships.</p></div>
              ) : (
                <div className="org-detail-grid">
                  <section className="org-panel org-overview">
                    <div className="org-panel-heading"><div><p className="org-eyebrow">Membership</p><h2>{current.name}</h2></div><span className={`org-role org-role-${current.role}`}>{roleLabel(current.role)}</span></div>
                    <p>{current.role === "owner" ? "You created this organisation and manage its membership." :
                      current.role === "admin" ? "You can manage invitations and ordinary members." :
                        "You are an active member of this organisation."}</p>
                    <button type="button" className="org-button org-button-primary org-library-link" onClick={() => navigate(`/organisations/${encodeURIComponent(current.organisationId)}/library`)}>
                      <LibraryBig size={17} /> Open Shared Library <ArrowRight size={16} />
                    </button>
                    {current.role === "owner" ? <div className="org-owner-note"><ShieldCheck size={18} /> Owners cannot leave until ownership transfer is available.</div> :
                      <button type="button" className="org-button org-button-outline-danger" onClick={() => openAction("leave")}>Leave organisation</button>}
                  </section>

                  {isManager(current.role) && <>
                    <section className="org-panel org-code-panel">
                      <div className="org-panel-heading"><div><p className="org-eyebrow">Invite teachers</p><h2>Join code</h2></div><span className="org-heading-icon"><KeyRound size={20} /></span></div>
                      <p>Share this code with a teacher. Their request will wait for approval.</p>
                      <div className="org-code-value"><code>{codeOverride || current.joinCode}</code></div>
                      <div className="org-inline-actions"><button type="button" className="org-button org-button-primary" onClick={copyCode}><ClipboardCopy size={16} /> Copy code</button>
                        <button type="button" className="org-button org-button-quiet" onClick={() => openAction("rotate")} disabled={actionBusy}><RefreshCw size={16} /> Rotate code</button></div>
                    </section>
                    <section className="org-panel org-wide">
                      <div className="org-panel-heading"><div><p className="org-eyebrow">Awaiting review</p><h2>Join requests</h2></div><span className="org-count">{requests.length}</span></div>
                      {detailLoading ? <p className="org-muted">Loading requests…</p> : filteredRequests.length ? <div className="org-rows">{filteredRequests.map((request) => <div className="org-row" key={request.requestId}>
                        <span className="org-person-icon"><Clock3 size={19} /></span><div className="org-row-copy"><strong>{personName(request)}</strong><small>Teacher · Awaiting approval</small></div>
                        <div className="org-row-actions"><button type="button" className="org-button org-button-primary" disabled={actionBusy} onClick={() => runAction({ type: "approve", requestId: request.requestId })}><Check size={16} /> Approve</button>
                          <button type="button" className="org-button org-button-quiet" disabled={actionBusy} onClick={() => openAction("decline", request)}>Decline</button></div>
                      </div>)}</div> : <p className="org-empty-line">{search ? "No matching requests." : "No requests are waiting for approval."}</p>}
                    </section>
                    <section className="org-panel org-wide">
                      <div className="org-panel-heading"><div><p className="org-eyebrow">People</p><h2>Members</h2></div><span className="org-count">{members.length}</span></div>
                      {detailLoading ? <p className="org-muted">Loading members…</p> : filteredMembers.length ? <div className="org-rows">{filteredMembers.map((member) => <div className="org-row" key={member.userUid}>
                        <span className="org-person-icon"><UsersRound size={19} /></span><div className="org-row-copy"><strong>{personName(member)}</strong><small>{member.userUid === user?.uid ? "You" : "Teacher"}</small></div>
                        <span className={`org-role org-role-${member.role}`}>{roleLabel(member.role)}</span>
                        {member.role === "member" && member.userUid !== user?.uid && <button type="button" className="org-button org-button-outline-danger" disabled={actionBusy} onClick={() => openAction("remove", member)}>Remove</button>}
                      </div>)}</div> : <p className="org-empty-line">{search ? "No matching members." : "No members to show."}</p>}
                    </section>
                  </>}
                </div>
              )}
            </>
          ) : (
            <div className="org-list-layout">
              <section className="org-panel org-list-panel">
                <div className="org-panel-heading"><div><p className="org-eyebrow">Your spaces</p><h2>My organisations</h2></div><span className="org-count">{organisations.length}</span></div>
                {loading ? <p className="org-muted">Loading your organisations…</p> : filtered.length ? <div className="org-card-list">{filtered.map((item) => <button key={item.organisationId} type="button" className="org-card" onClick={() => { setCodeOverride(""); setNotice(""); navigate(`/organisations/${encodeURIComponent(item.organisationId)}`); }}>
                  <span className="org-card-icon"><Building2 size={23} /></span><span className="org-card-copy"><strong>{item.name}</strong><small>{isManager(item.role) ? "Manage membership and invitations" : "View your membership"}</small></span><span className={`org-role org-role-${item.role}`}>{roleLabel(item.role)}</span><ArrowRight size={18} /></button>)}</div> :
                  <div className="org-empty"><span><Building2 size={29} /></span><h3>{search ? "No matching organisations" : "Your first organisation starts here"}</h3><p>{search ? "Try another name in the search box." : "Create a space for your colleagues, or use a code to request access to one."}</p></div>}
              </section>
              <div className="org-form-stack">
                <section className="org-panel org-form-panel"><span className="org-form-icon"><Plus size={21} /></span><h2>Create an organisation</h2><p>Start a space for teachers. You will be its owner.</p>
                  <form onSubmit={submitCreate}><label htmlFor="org-name">Organisation name</label><input id="org-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} placeholder="e.g. Science department" disabled={Boolean(formBusy)} />
                    <button type="submit" className="org-button org-button-primary" disabled={Boolean(formBusy)}>{formBusy === "create" ? "Creating…" : "Create organisation"}<ArrowRight size={16} /></button></form>
                </section>
                <section className="org-panel org-form-panel"><span className="org-form-icon org-form-icon-purple"><KeyRound size={21} /></span><h2>Join an organisation</h2><p>Enter a code from a colleague. A manager will approve your request before you join.</p>
                  <form onSubmit={submitJoin}><label htmlFor="org-join-code">Join code</label><input id="org-join-code" value={joinCode} onChange={(event) => setJoinCode(event.target.value)} placeholder="ORG-…" autoComplete="off" disabled={Boolean(formBusy)} />
                    <button type="submit" className="org-button org-button-secondary" disabled={Boolean(formBusy)}>{formBusy === "join" ? "Submitting…" : "Request to join"}<ArrowRight size={16} /></button></form>
                </section>
              </div>
            </div>
          )}
        </main>
      </div>
      <ConfirmationDialog action={confirmation} busy={actionBusy} onCancel={() => setConfirmation(null)} onConfirm={() => runAction(confirmation)} />
    </div>
  );
}
